import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { db } from './firebaseConfig';
import { collection, onSnapshot, query, where, doc, getDoc, getDocs, updateDoc, orderBy, limit } from 'firebase/firestore';
import { marco, imprimirResumoUmaVez } from '../utils/perfTrace';
import { ADMIN_STORE_EVENT } from '../utils/adminSession';
import { useUser } from './UserContext';
import { isEqual, omit } from 'lodash';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getApiUrl } from './appConfig';
import { isOrderAwaitingPayment } from '../utils/orderStatus';

// Create the context
const OrdersContext = createContext();

// Provider component
export const OrdersProvider = ({ children }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Loja que está sendo operada (MODO ADMIN). Precisa ser ESTADO, não uma leitura
     * direta do localStorage: entrar numa loja e sair dela são `navigate()`, sem reload
     * de página — sem reagir ao evento, os pedidos continuariam sendo os da loja
     * anterior até alguém apertar F5.
     */
    const [lojaOperada, setLojaOperada] = useState(() => {
        try { return localStorage.getItem('selectedStoreUid'); } catch { return null; }
    });

    useEffect(() => {
        const sincronizar = () => {
            try { setLojaOperada(localStorage.getItem('selectedStoreUid')); } catch { /* modo privado */ }
        };
        window.addEventListener(ADMIN_STORE_EVENT, sincronizar); // esta aba
        window.addEventListener('storage', sincronizar);          // outras abas
        return () => {
            window.removeEventListener(ADMIN_STORE_EVENT, sincronizar);
            window.removeEventListener('storage', sincronizar);
        };
    }, []);
    const { uid, authLoading } = useUser();
    const previousNewOrdersRef = useRef(new Set());
    const notifiedOrdersRef = useRef(new Set());
    const activeSoundAlertsRef = useRef(new Map());
    const isProcessingOrderRef = useRef(false);
    const previousAllocatedOrdersRef = useRef(new Set());
    const notifiedDriverAllocationRef = useRef(new Set());

    // Função para tocar som de alerta
    const playAlertSound = useCallback((orderId) => {

        try {
            // Evitar alertas duplicados
            if (activeSoundAlertsRef.current.has(orderId)) {

                return;
            }

            const audio = new Audio('/notification.wav');
            audio.loop = false;
            audio.volume = 0.5;

            activeSoundAlertsRef.current.set(orderId, audio);

            // Tentar tocar o som
            audio.play().then(() => {

            }).catch(err => {

                audio.loop = false; // Desabilitar loop se erro
            });
        } catch (error) {

        }
    }, []);



    // Função para mostrar notificações visuais
    const showNotification = useCallback((order) => {
        // Verificar se já notificou este pedido
        if (notifiedOrdersRef.current.has(order.id)) {
            return; // Já notificou este pedido
        }

        // Marcar como notificado
        notifiedOrdersRef.current.add(order.id);

        // Iniciar alerta sonoro
        playAlertSound(order.id);

        // Determinar o tipo de pedido para a mensagem
        const orderTypeText = order.isIfoodOrder ? 'iFood' : 'Auuii';
        const tagText = order.isIfoodOrder ? 'ifood' : 'auuii';

        // Tentar notificação visual
        if ('Notification' in window && Notification.permission === 'granted') {
            // Mostrar notificação sempre (navegador decide se modal ou popup)
            const notification = new Notification(`🆕 Novo Pedido - ${orderTypeText} #${order.id.substring(0, 6)}`, {
                body: `Cliente: ${order.customerName}\nValor: R$ ${order.total.toFixed(2)}\nClique para abrir`,
                icon: '/favicon.ico',
                tag: `${tagText}-order-${order.id}`,
                requireInteraction: true,
                silent: false,
            });

            notification.onclick = () => {
                window.focus();
                notification.close();
                window.location.href = '/cardapio-ifood';
            };
        } else if ('Notification' in window && Notification.permission === 'default') {
            // Usar confirm para ser a primeira interação que unlocks autoplay
            const allowSounds = window.confirm(`Alerta de pedido com som\n\nNovo Pedido - ${orderTypeText}\nCliente: ${order.customerName}\nValor: R$ ${order.total.toFixed(2)}\n\nPermito ativar notificações visuais E ALERTAS SONOROS?`);
            if (allowSounds) {
                Notification.requestPermission().then(permission => {
                    if (permission === 'granted') {
                        // Interação anterior (confirm) pode ter desbloqueado autoplay
                    }
                });
            }
            // Mesmo se cancelar, som tentará tocar novamente na próxima vez após interação
        } else if ('Notification' in window && Notification.permission !== 'denied') {
            // Solicitar permissão
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    // Requisição foi atendida
                }
            });
        } else {
            // Permissões negadas, mostrar apenas alerta de som (já iniciado acima)
        }
    }, [playAlertSound]);

    useEffect(() => {
        if (authLoading) return;

        // Captura ref atual para cleanup (resolve ESLint warning)
        const currentActiveSoundAlerts = activeSoundAlertsRef.current;

        if (!uid) {
            setOrders([]);
            setLoading(false);
            setError(null);

            // Cleanup: parar todos os sons quando user desloga
            currentActiveSoundAlerts.forEach((audio) => {
                try {
                    audio.loop = false;
                    audio.pause();
                    audio.currentTime = 0;
                } catch (e) {
                    // Ignora erros no cleanup
                }
            });
            currentActiveSoundAlerts.clear();
            notifiedDriverAllocationRef.current.clear();

            return;
        }

        // A LOJA OPERADA, não necessariamente a conta logada.
        //
        // Um admin usando "MODO ADMIN — operando a loja X" continua autenticado com o
        // PRÓPRIO uid; a loja escolhida vive em `selectedStoreUid` (ver utils/adminSession.js),
        // que Home, CallCourier, EditarLoja e TodoScreen já respeitam. Aqui e na tela de
        // Pedidos isso tinha passado batido: as permissões eram resolvidas pelo uid do admin,
        // que não é gerente de loja nenhuma — resultado: "Nenhum restaurante disponível" e
        // zero pedidos, exatamente para quem mais precisa dessa tela (o suporte).
        const userId = lojaOperada || uid;
        let isFirstLoad = true;
        let auuiiOrders = [];
        let ifoodOrders = [];
        let auuiiLoaded = false;
        let ifoodLoaded = false;

        // Get all merchant IDs the user has access to
        const getAllowedMerchantIds = async () => {
            try {
                const ids = new Set();
                ids.add(userId);

                // As duas leituras abaixo são independentes — em fila, uma esperava a
                // outra sem motivo, e cada ida ao Firestore custa uma volta de rede.
                marco('permissoes:consultando o Firestore');
                const merchantsRef = collection(db, "merchants");
                const q = query(merchantsRef, where("managers", "array-contains", userId));
                const [snap, userDocPreCarregado] = await Promise.all([
                    getDocs(q),
                    getDoc(doc(db, "merchants", userId)),
                ]);
                
                snap.forEach(doc => {
                    ids.add(doc.id.trim());
                    const data = doc.data();
                    if (data.ifoodMerchantId) {
                        if (Array.isArray(data.ifoodMerchantId)) {
                            data.ifoodMerchantId.forEach(id => ids.add(id.trim()));
                        } else {
                            ids.add(data.ifoodMerchantId.trim());
                        }
                    }
                });

                // Also check the user's own merchant doc (já veio no Promise.all acima)
                const userDoc = userDocPreCarregado;
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    if (data.ifoodMerchantId) {
                        if (Array.isArray(data.ifoodMerchantId)) {
                            data.ifoodMerchantId.forEach(id => ids.add(id.trim()));
                        } else {
                            ids.add(data.ifoodMerchantId.trim());
                        }
                    }
                }

                // Adicionar versões em minúsculo e maiúsculo para garantir
                const finalIds = new Set();
                ids.forEach(id => {
                    finalIds.add(id);
                    finalIds.add(id.toLowerCase());
                    finalIds.add(id.toUpperCase());
                });

                return Array.from(finalIds);
            } catch (error) {
                console.error('Erro ao obter IDs de merchants:', error);
                return [userId];
            }
        };

        const processOrdersData = (querySnapshot, isIfoodQuery = false) => {
            const ordersData = querySnapshot.docs
                .filter(doc => !isOrderAwaitingPayment(doc.data()))
                .map(doc => {
                const data = doc.data();
                // Tenta encontrar a data de criação real em vários campos possíveis
                const rawCreatedAt = data.createdAt || data.orderTimestamp || data.orderedAt || data.details?.createdAt || data.details?.orderTimestamp;
                let finalCreatedAt;
                
                if (rawCreatedAt?.toDate) {
                    finalCreatedAt = rawCreatedAt.toDate();
                } else if (rawCreatedAt) {
                    finalCreatedAt = new Date(rawCreatedAt);
                } else {
                    finalCreatedAt = new Date(); // Fallback final
                }

                return {
                    id: doc.id,
                    ...data,
                    createdAt: finalCreatedAt,
                    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt ? new Date(data.updatedAt) : null),
                    items: Array.isArray(data.items) ? data.items : [],
                    // Preservar o valor numérico para compatibilidade, mas extrair componentes
                    subTotal: Number(data.subTotal) || Number(data.total?.subTotal) || Number(data.cartTotal) || 0,
                    deliveryFee: Number(data.deliveryFee) || Number(data.total?.deliveryFee) || Number(data.delivery?.fee) || Number(data.delivery?.deliveryFee) || 0,
                    discount: Number(data.discount) || Number(data.total?.benefits) || 0,
                    total: typeof data.total === 'object' 
                        ? (data.total?.orderAmount || data.totalAmount || 0) 
                        : (Number(data.total) || data.totalAmount || 0),
                    status: data.status || 'pendente',
                    customerName: (typeof data.customerName === 'string' ? data.customerName : (data.customer?.name || data.details?.customer?.name || data.User?.name || (data.source === 'ifood' ? 'Pedido iFood' : 'Cliente não identificado'))),
                    customerAddress: data.customerAddress || data.delivery?.address?.formattedAddress || data.details?.delivery?.address?.formattedAddress || data.User?.address || 'Endereço não informado',
                    customerPhone: (typeof data.customerPhone === 'string' ? data.customerPhone : (data.customer?.phone?.number || data.details?.customer?.phone?.number || data.User?.phone || 'Telefone não informado')),
                    paymentMethod: data.paymentMethod || data.payments?.[0]?.method || 'Não especificado',
                    isIfoodOrder: !!data.merchantId || data.source?.toLowerCase() === 'ifood' || data.salesChannel?.toLowerCase() === 'ifood' || isIfoodQuery,
                    isAuuiiOrder: !data.merchantId && data.source?.toLowerCase() !== 'ifood' && data.salesChannel?.toLowerCase() !== 'ifood',
                    // Incluir dados do motoboy/entregador se disponível
                    current: data.current || null,
                    User: data.User || null,
                    Restaurant: data.Restaurant || null
                };
            });

            return ordersData;
        };

        const handleOrdersUpdate = (ordersData, isInitialLoad = false) => {
            // DETECTAR ALOCAÇÃO DE MOTOBOY
            const currentAllocatedOrders = new Set();
            ordersData.forEach(order => {
                const hasDriver = !!(
                    order.driverName || order.driver?.name || order.current?.driver?.name || 
                    order.assignedDriverName || order.allocation?.driverName || 
                    order.driverId || order.assignedDriverId
                );
                const statusLower = (order.status || '').toLowerCase();
                const isFinal = ['concluido', 'concluído', 'entregue', 'cancelado', 'cancelled', 'can', 'completed'].includes(statusLower);
                if (hasDriver && !isFinal) {
                    currentAllocatedOrders.add(order.id);
                }
            });

            if (!isInitialLoad) {
                currentAllocatedOrders.forEach(orderId => {
                    if (!previousAllocatedOrdersRef.current.has(orderId) && !notifiedDriverAllocationRef.current.has(orderId)) {
                        const order = ordersData.find(o => o.id === orderId);
                        const driverName = order.driverName || order.driver?.name || order.current?.driver?.name || order.assignedDriverName || order.allocation?.driverName || 'um entregador';
                        toast.info(`Motoboy a caminho! ${driverName} foi alocado para o pedido #${order.id.substring(0,6).toUpperCase()}.`, {
                            position: "top-right",
                            autoClose: 5000,
                        });
                        notifiedDriverAllocationRef.current.add(orderId);
                    }
                });
            }
            previousAllocatedOrdersRef.current = currentAllocatedOrders;

            // Detectar novos pedidos (NEW_ORDER para iFood, pendente para Auuii; PENDING_PAYMENT fica oculto até aprovar) para notificações
            const alertStatuses = ['NEW_ORDER', 'pendente'];
            const currentNewOrders = new Set(
                ordersData.filter(order => alertStatuses.includes(order.status)).map(order => `${order.id}-${order.status}`)
            );

            let newOrderKeys;
            if (isInitialLoad) {
                // Na carga inicial, apenas registrar os pedidos existentes SEM notificação sonora
                newOrderKeys = []; // Não notificar na carga inicial
            } else {
                // Em atualizações, só considerar realmente novos
                newOrderKeys = [...currentNewOrders].filter(orderKey => !previousNewOrdersRef.current.has(orderKey));
            }

            // Mostrar notificações para novos pedidos ou mudanças para status de alerta
            newOrderKeys.forEach(orderKey => {
                const lastDashIndex = orderKey.lastIndexOf('-');
                const orderId = orderKey.substring(0, lastDashIndex);
                const order = ordersData.find(o => o.id === orderId);
                if (order) {
                    showNotification(order);
                }
            });

            // LÓGICA DE AUTO-STOP: Parar sons de pedidos que NÃO estão mais em status de alerta
            const activeSounds = activeSoundAlertsRef.current;
            activeSounds.forEach((audio, orderId) => {
                const order = ordersData.find(o => o.id === orderId);
                // Se o pedido não existe mais na lista ou mudou para um status que não está em alertStatuses
                if (!order || !alertStatuses.includes(order.status)) {
                    try {
                        audio.loop = false;
                        audio.pause();
                        audio.currentTime = 0;
                        activeSounds.delete(orderId);
                    } catch (e) {
                        console.error('Erro ao parar som automático:', e);
                    }
                }
            });

            previousNewOrdersRef.current = currentNewOrders;

            setOrders(prevOrders => {
                if (prevOrders.length === 0 && ordersData.length === 0) return prevOrders;
                
                // Função para limpar campos que mudam frequentemente mas não devem causar re-render na lista inteira
                // Ignoramos o objeto 'current' quase por completo, pois ele contém GPS, distância e ETA que mudam a cada segundo
                const simplify = (list) => list.map(o => ({
                    ...omit(o, ['updatedAt', 'createdAt', 'current']),
                    driverStatus: o.current?.status,
                    driverUid: o.current?.uid
                }));
                
                const prevSimplified = simplify(prevOrders);
                const nextSimplified = simplify(ordersData);
                
                if (isEqual(prevSimplified, nextSimplified)) {
                    return prevOrders; // Se apenas a localização do motoboy mudou, não re-renderiza a lista inteira
                }

                // DEBUGGING: Log o que mudou (Removido para produção)

                return ordersData;
            });

            // Para carga inicial, manter loading=true até primeira notificação
            if (!isInitialLoad || newOrderKeys.length === 0) {
                setLoading(false);
            }
            setError(null);
        };


        /**
         * Manda pra tela o que JÁ chegou, sem esperar o outro listener.
         *
         * Antes cada handler só publicava `if (oOutroJaCarregou)` — a tela ficava em
         * "carregando" até os DOIS lotes chegarem (até 300 documentos cada), mesmo com um
         * deles pronto há segundos. E se um dos listeners falhava, o outro nunca publicava.
         *
         * `isInitialLoad` continua verdadeiro enquanto os dois não chegaram: é ele que
         * silencia o som de "novo pedido" e o toast de motoboy alocado. Sem esse cuidado,
         * o segundo lote entraria como se fossem pedidos recém-chegados e o painel
         * apitaria sozinho a cada F5.
         */
        const publicarPedidos = () => {
            const ambosChegaram = auuiiLoaded && ifoodLoaded;
            handleOrdersUpdate([...auuiiOrders, ...ifoodOrders], isFirstLoad || !ambosChegaram);
            if (ambosChegaram) {
                if (isFirstLoad) {
                    marco('pedidos:tela liberada', `${auuiiOrders.length + ifoodOrders.length} pedidos`);
                    imprimirResumoUmaVez();
                }
                isFirstLoad = false;
            }
        };
        // Setup queries for all allowed merchants
        getAllowedMerchantIds().then(allowedIds => {
            if (allowedIds.length === 0) {
                setLoading(false);
                return;
            }

            // Slice allowedIds for 'in' query (Firestore limit is 30, but let's be safe)
            const queryIds = allowedIds.slice(0, 30);

            // Janela de 7 dias ao vivo (corta drasticamente a leitura: antes o
            // listener lia TODOS os pedidos já existentes e relia a cada mudança).
            const seteDiasAtras = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

            // 1. Auuii Orders Query (Restaurant.id in allowedIds)
            const auuiiQuery = query(
                collection(db, 'order'),
                where('Restaurant.id', 'in', queryIds),
                where('createdAt', '>=', seteDiasAtras),
                orderBy('createdAt', 'desc'),
                limit(300)
            );

            const unsubscribeAuuii = onSnapshot(
                auuiiQuery,
                (querySnapshot) => {
                    auuiiOrders = processOrdersData(querySnapshot);
                    if (!auuiiLoaded) marco('pedidos:1o lote Auuii', `${querySnapshot.size} documentos`);
                    auuiiLoaded = true;
                    publicarPedidos();
                },
                (error) => {
                    console.error('❌ [OrdersContext] Erro ao buscar pedidos Auuii:', error);
                    setError('Erro ao carregar pedidos Auuii');
                    marco('pedidos:Auuii FALHOU', error?.code || error?.message);
                    auuiiLoaded = true; // Proceed if Auuii fails
                    publicarPedidos();
                }
            );

            // 2. iFood Orders Query (merchantId in allowedIds)
            const ifoodQuery = query(
                collection(db, 'order'),
                where('merchantId', 'in', queryIds),
                where('createdAt', '>=', seteDiasAtras),
                orderBy('createdAt', 'desc'),
                limit(300)
            );

            const unsubscribeIfoodSub = onSnapshot(
                ifoodQuery,
                (querySnapshot) => {
                    ifoodOrders = processOrdersData(querySnapshot, true);
                    if (!ifoodLoaded) marco('pedidos:1o lote iFood', `${querySnapshot.size} documentos`);
                    ifoodLoaded = true;
                    publicarPedidos();
                },
                (error) => {
                    console.error('Erro ao buscar pedidos iFood:', error);
                    marco('pedidos:iFood FALHOU', error?.code || error?.message);
                    ifoodLoaded = true; // Proceed if iFood fails
                    publicarPedidos();
                }
            );

            // Update cleanups
            const originalCleanup = cleanup;
            cleanup = () => {
                if (originalCleanup) originalCleanup();
                unsubscribeAuuii();
                unsubscribeIfoodSub();
            };

        }).catch((err) => {
            console.error('Erro ao configurar queries de pedidos:', err);
            setLoading(false);
        });

        let cleanup = () => {
            // Emergency cleanup: parar todos sons ativos
            currentActiveSoundAlerts.forEach((audio) => {
                try {
                    audio.loop = false;
                    audio.pause();
                    audio.currentTime = 0;
                } catch (e) {
                    // Ignora erros no cleanup
                }
            });
            currentActiveSoundAlerts.clear();
            isProcessingOrderRef.current = false;
        };

        return () => cleanup();
    }, [uid, lojaOperada, authLoading, showNotification]);



    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            const orderRef = doc(db, 'order', orderId);
            await updateDoc(orderRef, {
                status: newStatus,
                updatedAt: new Date()
            });
        } catch (error) {
            console.error('Erro ao atualizar status do pedido:', error);
            throw error;
        }
    };

    const rejectOrder = async (orderId) => {
        // Parar o som do alerta antes de rejeitar
        try {
            // 🌐 Busca o merchantId se disponível
            const order = orders.find(o => o.id === orderId);
            const mId = order?.merchantId || order?.Restaurant?.id;

            // 🌐 Chama o backend primeiro
            await axios.post(`${getApiUrl()}/api/orders/${orderId}/reject`, { merchantId: mId }, { timeout: 15000 });
            
            await updateOrderStatus(orderId, 'REJECTED');
            // Parar TODOS os sons após salvar
            const currentSounds = activeSoundAlertsRef.current;
            currentSounds.forEach((audio, id) => {
                try {
                    audio.loop = false;
                    audio.pause();
                    audio.currentTime = 0;
                } catch (audioError) {
                    console.error(`❌ Erro ao parar audio para ${id}:`, audioError);
                }
            });
            currentSounds.clear();
        } catch (error) {
            console.error('Erro ao rejeitar pedido:', error);
            throw error;
        }
    };

    // Função para parar TODOS os áudios possíveis (nuclear option)
    const stopAllAudioGlobally = (source = 'unknown') => {
        let stoppedCount = 0;

        // 1. Parar áudios do nosso Map
        activeSoundAlertsRef.current.forEach((audio, id) => {
            try {
                audio.loop = false;
                audio.pause();
                audio.currentTime = 0;
                audio.volume = 0;
                if (audio.parentNode) {
                    audio.parentNode.removeChild(audio);
                }
                stoppedCount++;
            } catch (e) {
                console.error('🧨 Erro ao parar audio do Map:', e);
            }
        });
        activeSoundAlertsRef.current.clear();

        // 2. Buscar e parar TODOS os elementos audio na página
        const allAudioElements = document.querySelectorAll('audio');
        allAudioElements.forEach((audio, index) => {
            try {
                audio.loop = false;
                audio.pause();
                audio.currentTime = 0;
                audio.volume = 0;
                audio.muted = true;
                stoppedCount++;
            } catch (e) {
                console.error('🧨 Erro ao parar elemento audio:', e);
            }
        });

        // 3. Buscar outras possibilidades de áudio
        const mediaElements = document.querySelectorAll('video, embed, object');
        mediaElements.forEach((element, index) => {
            try {
                if (element.pause) {
                    element.pause();

                }
            } catch (e) {
                console.error('🧨 Erro ao pausar mídia:', e);
            }
        });


        return stoppedCount;
    };

    const acceptOrder = async (orderId) => {
        const isReloadMode = true; // Manter reload para solução garantida

        // Parar o áudio específico do pedido aceitado
        const activeSounds = activeSoundAlertsRef.current;
        const audio = activeSounds.get(orderId);
        if (audio) {
            try {
                audio.loop = false;
                audio.pause();
                audio.currentTime = 0;
                audio.volume = 0;
                if (audio.parentNode) {
                    audio.parentNode.removeChild(audio);
                }
                activeSounds.delete(orderId);

            } catch (audioError) {
                console.error(`❌ Erro ao parar som para ${orderId}:`, audioError);
            }
        }

        if (isReloadMode) {
            // Salvar status e recarregar (garante paradas de áudios)
            isProcessingOrderRef.current = true;
            try {
                // 🌐 Busca o merchantId se disponível
                const order = orders.find(o => o.id === orderId);
                const mId = order?.merchantId || order?.Restaurant?.id;

                // 🌐 Chama o backend primeiro
                await axios.post(`${getApiUrl()}/api/orders/${orderId}/confirm`, { merchantId: mId }, { timeout: 20000 });

                await updateOrderStatus(orderId, 'ACCEPTED');
                setTimeout(() => window.location.reload(), 300);
            } catch (error) {
                // Reverter cambiamentiod se erro no update
                throw error;
            } finally {
                isProcessingOrderRef.current = false;
            }
        } else {
            // 🎵 MODO GRÁCIL - Sem recarregar página

            try {
                // 🧨 Parada massiva de áudios primeiro
                stopAllAudioGlobally('acceptOrder-grace');

                // 🌐 Tentar fechar Web Audio API (se houver)
                if (window.AudioContext || window.webkitAudioContext) {
                    try {
                        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                        if (audioCtx.state === 'running') {
                            await audioCtx.close();
                        }
                    } catch (e) {
                        // API pode não estar disponível
                    }
                }

                // Flag para impedir notificações concorrentes
                isProcessingOrderRef.current = true;

                // 🌐 Busca o merchantId se disponível
                const order = orders.find(o => o.id === orderId);
                const mId = order?.merchantId || order?.Restaurant?.id;

                // 🌐 Chama o backend primeiro
                await axios.post(`${getApiUrl()}/api/orders/${orderId}/confirm`, { merchantId: mId }, { timeout: 20000 });

                // 🔄 Atualizar status no Firebase
                await updateOrderStatus(orderId, 'ACCEPTED');

            } catch (error) {
                console.error('❌ Erro no processamento:', error);
                // Em caso de erro grave, fazer reload como backup
                setTimeout(() => window.location.reload(), 300);
                throw error;
            } finally {
                // Reset flag sempre
                isProcessingOrderRef.current = false;
            }
        }
    };

    const markOrderReady = async (orderId) => {
        // 🌐 Busca o merchantId se disponível
        const order = orders.find(o => o.id === orderId);
        const mId = order?.merchantId || order?.Restaurant?.id;

        // 🌐 Chama o backend primeiro
        await axios.post(`${getApiUrl()}/api/orders/${orderId}/readyToPickup`, { merchantId: mId }, { timeout: 15000 });
        await updateOrderStatus(orderId, 'READY');
    };

    const dispatchOrder = async (orderId) => {
        // 🌐 Busca o merchantId se disponível
        const order = orders.find(o => o.id === orderId);
        const mId = order?.merchantId || order?.Restaurant?.id;

        // 🌐 Chama o backend primeiro
        await axios.post(`${getApiUrl()}/api/orders/${orderId}/dispatch`, { merchantId: mId }, { timeout: 15000 });
        await updateOrderStatus(orderId, 'DISPATCHED');
    };

    const value = {
        orders,
        loading,
        error,
        updateOrderStatus,
        rejectOrder,
        acceptOrder,
        markOrderReady,
        dispatchOrder
    };

    return (
        <OrdersContext.Provider value={value}>
            {children}
        </OrdersContext.Provider>
    );
};

// Custom hook to use the OrdersContext
export const useOrders = () => {
    const context = useContext(OrdersContext);
    if (!context) {
        throw new Error('useOrders deve ser usado dentro de um OrdersProvider');
    }
    return context;
};
